import type {ReactNode} from 'react'
import {useEffect, useState} from 'react'

export type TypewriterSpeed = 'fast' | 'normal' | 'slow'

const SPEED_CONFIG = {
  fast: 20,
  normal: 40,
  slow: 60,
}

export const useTypewriterHTML = (htmlContent: string, speed: TypewriterSpeed = 'normal') => {
  const [displayedContent, setDisplayedContent] = useState('')
  const [isComplete, setIsComplete] = useState(false)

  // Typewriter animation effect
  useEffect(() => {
    if (!htmlContent) {
      return
    }

    // Parse HTML to separate text from tags
    const parser = new DOMParser()
    const doc = parser.parseFromString(htmlContent, 'text/html')
    const textContent = doc.body.textContent || ''
    const totalChars = textContent.length

    let currentChar = 0
    const intervalMs = SPEED_CONFIG[speed]

    const interval = setInterval(() => {
      if (currentChar <= totalChars) {
        // Walk through the DOM and build HTML string up to current character position
        let charCount = 0
        let result = ''

        const walkNode = (node: Node): boolean => {
          if (node.nodeType === Node.TEXT_NODE) {
            const text = node.textContent || ''
            const charsToAdd = Math.min(text.length, currentChar - charCount)

            if (charsToAdd > 0) {
              result += text.slice(0, charsToAdd)
              charCount += charsToAdd
            }

            return charCount >= currentChar
          } else if (node.nodeType === Node.ELEMENT_NODE) {
            const element = node as Element
            const tagName = element.tagName.toLowerCase()

            const attributes = Array.from(element.attributes)
              .map((attr) => `${attr.name}="${attr.value}"`)
              .join(' ')

            result += `<${tagName}${attributes ? ' ' + attributes : ''}>`

            for (const child of Array.from(node.childNodes)) {
              if (walkNode(child)) {
                result += `</${tagName}>`
                return true
              }
            }

            result += `</${tagName}>`
          }

          return false
        }

        for (const child of Array.from(doc.body.childNodes)) {
          if (walkNode(child)) {
            break
          }
        }

        setDisplayedContent(result)
        currentChar++
      } else {
        setIsComplete(true)
        clearInterval(interval)
      }
    }, intervalMs)

    return () => clearInterval(interval)
  }, [htmlContent, speed])

  return {displayedContent, isComplete}
}

export const useTypewriterSegments = (
  segments: Array<{text: string; isBold: boolean}>,
  speed: TypewriterSpeed = 'normal',
) => {
  const [charCount, setCharCount] = useState(0)
  const [isComplete, setIsComplete] = useState(false)

  const totalChars = segments.reduce((acc, seg) => acc + seg.text.length, 0)

  // Typewriter animation effect: incrementally increases character count for segment-based rendering
  // Character count is used by renderTypewriterText to progressively reveal text segments
  useEffect(() => {
    if (!totalChars) {
      return
    }

    let currentChar = 0
    const intervalMs = SPEED_CONFIG[speed]

    const interval = setInterval(() => {
      if (currentChar <= totalChars) {
        setCharCount(currentChar)
        currentChar++
      } else {
        setIsComplete(true)
        clearInterval(interval)
      }
    }, intervalMs)

    return () => clearInterval(interval)
  }, [totalChars, speed])

  const renderTypewriterText = (): ReactNode => {
    let displayedChars = 0
    const elements: ReactNode[] = []

    segments.forEach((segment, segIndex) => {
      const segmentStart = displayedChars
      const segmentEnd = displayedChars + segment.text.length
      const visibleLength = Math.max(0, Math.min(charCount - segmentStart, segment.text.length))
      const visibleText = segment.text.slice(0, visibleLength)

      if (visibleText) {
        const element = segment.isBold ? (
          <strong key={segIndex} className="font-medium text-moss-black">
            {visibleText}
          </strong>
        ) : (
          <span key={segIndex}>{visibleText}</span>
        )

        elements.push(element)
      }

      displayedChars = segmentEnd
    })

    return elements
  }

  return {
    charCount,
    isComplete,
    totalChars,
    renderTypewriterText,
  }
}
